import {
  Calendar,
  ChevronRight,
  FileText,
  Mail,
  Paperclip,
} from 'lucide-react';

import type { Communication } from '@/api/fe-api/entities/communication';
import { cls } from '@/utils/cls';
import { formatDatetime } from '@/utils/format/date';
import { label } from '@/utils/format/label';

import { useDetailsId } from '../hooks/use-details-id';
import {
  formatParticipant,
  formatParticipants,
} from '../utils/format/participant';

export type CommunicationSidebarListItemProps = {
  communication: Communication;
};

export function CommunicationSidebarListItem({
  communication,
}: CommunicationSidebarListItemProps) {
  const [detailsId, setDetailsId] = useDetailsId();

  function handleClick() {
    setDetailsId(communication);
  }

  const isSelected = detailsId?.id === communication.id;
  const hasAttachments =
    (communication.type === 'email' || communication.type === 'meeting') &&
    communication.attachments.length > 0;
  const hasMeetingMinutes =
    communication.type === 'meeting' && communication.meetingMinutes.length > 0;

  return (
    <div
      role="button"
      onClick={handleClick}
      className={cls(
        'flex h-full min-h-20 cursor-pointer items-center px-4 py-3',
        isSelected ? 'bg-primary-50' : 'hover:bg-gray-100',
      )}
    >
      <div className="mr-3 shrink-0">
        <Icon communication={communication} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">
          {communication.type === 'email'
            ? communication.subject
            : communication.title}
        </p>
        <Preview communication={communication} />
      </div>
      <div className="ml-2 flex gap-2">
        {hasAttachments ? (
          <Paperclip className="size-3.5 text-gray-700" />
        ) : null}
        {hasMeetingMinutes ? (
          <FileText className="size-3.5 text-gray-700" />
        ) : null}
        <ChevronRight className="size-4 text-gray-400" />
      </div>
    </div>
  );
}

type PreviewProps = { communication: Communication };

function Preview({ communication }: PreviewProps) {
  switch (communication.type) {
    case 'email': {
      const to = [...communication.recipients, ...communication.cc];

      return (
        <div className="text-xs text-gray-500">
          <p>From: {formatParticipant(communication.sender)}</p>
          {to.length > 0 ? <p>To: {formatParticipants(to)}</p> : null}
        </div>
      );
    }
    case 'meeting': {
      return (
        <div className="text-xs text-gray-500">
          {communication.start ? (
            <p>{formatDatetime(communication.start)}</p>
          ) : null}
          {communication.attendees.length > 0 ? (
            <p>
              {communication.attendees.length}{' '}
              {label(['attendee', 'attendees'], communication.attendees.length)}
            </p>
          ) : null}
        </div>
      );
    }
  }
}

type IconProps = { communication: Communication };

function Icon({ communication }: IconProps) {
  switch (communication.type) {
    case 'email':
      return <Mail className="size-5 text-gray-600" />;
    case 'meeting':
      return <Calendar className="size-5 text-gray-600" />;
    default:
      return null;
  }
}
